import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Radio } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const ChannelsPage = () => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/channels`)
      .then(res => setChannels(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <main className="pt-10 pb-20 px-4 md:px-8">
        <div className="max-w-5xl mx-auto">
          <h1 className="font-secondary text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white mb-12 text-center">
            Channels
          </h1>

          {channels.length === 0 ? (
            <div className="text-center py-20">
              <Radio className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500">No channels available</p>
            </div>
          ) : (
            <div
              className="grid gap-6"
              style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}
              data-testid="channels-grid"
            >
              {channels.map((ch, i) => {
                const color = ch.primary_color || "#FFFFFF";
                return (
                  <motion.div
                    key={ch.channel_id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Link
                      to={`/channels/${ch.channel_id}`}
                      className="channel-block group flex flex-col items-center text-center"
                      data-testid={`channel-block-${ch.channel_id}`}
                    >
                      {/* Logo block */}
                      <div
                        className="relative w-full aspect-square bg-zinc-900/60 border border-white/8 flex items-center justify-center overflow-hidden transition-all duration-300 group-hover:border-transparent"
                        style={{
                          "--glow-color": color,
                        }}
                      >
                        {/* Glow overlay on hover */}
                        <div
                          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                          style={{
                            boxShadow: `inset 0 0 40px ${color}25, 0 0 30px ${color}20`,
                            border: `1px solid ${color}50`,
                          }}
                        />

                        {ch.logo_url ? (
                          <img
                            src={ch.logo_url}
                            alt={ch.name}
                            className="w-3/5 h-3/5 object-contain relative z-10 transition-transform duration-300 group-hover:scale-110"
                          />
                        ) : (
                          <span
                            className="text-5xl font-black font-secondary relative z-10 transition-transform duration-300 group-hover:scale-110"
                            style={{ color }}
                          >
                            {ch.name?.[0] || "?"}
                          </span>
                        )}

                        {/* LIVE badge */}
                        {ch.is_live && (
                          <div className="absolute top-2 right-2 z-20 flex items-center gap-1 px-2 py-0.5 bg-red-600 text-white text-[9px] font-bold uppercase">
                            <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                            LIVE
                          </div>
                        )}
                      </div>

                      {/* Channel name */}
                      <p
                        className="mt-3 text-sm font-bold uppercase tracking-wide text-zinc-400 transition-colors duration-300"
                        style={{
                          // tint text on hover via group-hover handled by inline style won't work, use CSS var approach
                        }}
                      >
                        <span className="group-hover:text-white transition-colors duration-300">
                          {ch.name}
                        </span>
                      </p>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ChannelsPage;
